import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Decode an arbitrary JWT payload field without verifying the signature
 * — that's the API's job. Returns undefined on any failure.
 */
function jwtPayload(token: string): Record<string, unknown> | undefined {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return undefined;
    return JSON.parse(
      atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')),
    ) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

function permissionsFromJwt(token: string): string[] {
  const p = jwtPayload(token);
  return Array.isArray(p?.['permissions']) ? (p!['permissions'] as string[]) : [];
}

/**
 * Role CODES from the token. Always decoded here rather than accepted from a
 * caller like `permissions` is: roles gate the few owner-only capabilities, and
 * a value the caller can pass in is a value the caller can get wrong.
 */
function rolesFromJwt(token: string): string[] {
  const p = jwtPayload(token);
  return Array.isArray(p?.['roles']) ? (p!['roles'] as string[]) : [];
}

function impersonationFromJwt(token: string): ImpersonationClaim | null {
  const p = jwtPayload(token);
  const imp = p?.['impersonation'];
  if (imp && typeof imp === 'object' && imp !== null) {
    return imp as ImpersonationClaim;
  }
  return null;
}

interface ImpersonationClaim {
  platformAdminId: string;
  reason: string;
  startedAt: string;
}

interface AuthUser {
  id: string;
  tenantId: string;
  email: string;
  fullName: string;
  locale: 'en' | 'ar';
  status: 'active' | 'invited' | 'disabled';
}

interface AuthTenant {
  id: string;
  name: string;
  slug: string;
  defaultLocale: 'en' | 'ar';
  baseCurrencyCode: string;
  logoUrl?: string | null;
  billingStatus?: 'trialing' | 'active' | 'past_due' | 'unpaid' | 'cancelled';
  currentPeriodEnd?: string | null;
  trialEndsAt?: string | null;
}

interface AuthState {
  accessToken: string | null;
  expiresAt: number | null;
  user: AuthUser | null;
  tenant: AuthTenant | null;
  permissions: string[];
  /** Role codes, for the handful of gates that must not be delegable. */
  roles: string[];
  /**
   * Set iff this session was minted via
   * POST /platform/tenants/:id/impersonate. Drives the impersonation
   * banner in AppLayout.
   */
  impersonation: ImpersonationClaim | null;
  setToken(token: string, expiresIn: number, permissions?: string[]): void;
  setSession(p: {
    accessToken: string;
    expiresIn: number;
    user: AuthUser;
    tenant: AuthTenant;
    permissions?: string[];
  }): void;
  clear(): void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      expiresAt: null,
      user: null,
      tenant: null,
      permissions: [],
      roles: [],
      impersonation: null,
      setToken: (token, expiresIn, permissions) =>
        set((s) => ({
          accessToken: token,
          expiresAt: Date.now() + expiresIn * 1000,
          // Prefer caller-supplied perms; fall back to the JWT claim; finally keep existing.
          permissions: permissions ?? permissionsFromJwt(token) ?? s.permissions,
          roles: rolesFromJwt(token),
          impersonation: impersonationFromJwt(token),
        })),
      setSession: ({ accessToken, expiresIn, user, tenant, permissions }) =>
        set({
          accessToken,
          expiresAt: Date.now() + expiresIn * 1000,
          user,
          tenant,
          // Backend doesn't return permissions at the top level; decode them from the JWT.
          permissions: permissions ?? permissionsFromJwt(accessToken),
          roles: rolesFromJwt(accessToken),
          impersonation: impersonationFromJwt(accessToken),
        }),
      clear: () =>
        set({
          accessToken: null,
          expiresAt: null,
          user: null,
          tenant: null,
          permissions: [],
          roles: [],
          impersonation: null,
        }),
    }),
    {
      // sessionStorage so closing the tab signs the user out, but a page refresh
      // (or in-app navigation that hard-reloads) keeps them in.
      name: 'erp-auth',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

/**
 * A WHOLE session: a token AND the person and shop it belongs to. A refresh only
 * mints a token, so a tab that had been signed out could come back from one
 * holding a token and nothing else — every screen then falls back to guesses
 * ("USD" for the shop's currency), and a rate typed there was saved backwards.
 * Anything deciding "signed in" asks this, not for the token alone.
 */
export function hasSession(s: Pick<AuthState, 'accessToken' | 'user' | 'tenant'>): boolean {
  return !!s.accessToken && !!s.user && !!s.tenant?.baseCurrencyCode;
}

export type { AuthUser, AuthTenant };
